/**
 * Heat grids (D529): a rows × columns grid of values bucketed into a few ordered LEVELS (the heat scale of
 * `@vello/tokens`), plus the calendar layout (weeks × weekdays) of day-keyed values. Pure: the caller injects the labels,
 * the week start of the market and the formatting; nothing here knows a locale or a country.
 */
import { extent, isFiniteNumber } from './numbers.js';
/** How many heat steps the token scale has. */
export const HEAT_STEPS = 5;
/** The level (1 … steps) of a value among ascending `thresholds`. */
export function heatLevel(value, thresholds) {
    let level = 1;
    for (const t of thresholds) {
        if (value >= t)
            level += 1;
        else
            break;
    }
    return level;
}
function quantileThresholds(values, steps) {
    const sorted = [...values].sort((a, b) => a - b);
    const out = [];
    for (let k = 1; k < steps; k += 1) {
        const at = (k / steps) * (sorted.length - 1);
        const lo = Math.floor(at);
        const hi = Math.ceil(at);
        const v = (sorted[lo] ?? 0) + ((sorted[hi] ?? 0) - (sorted[lo] ?? 0)) * (at - lo);
        out.push(v);
    }
    return out;
}
/** Bucket a grid of values into heat levels. Out-of-range inputs are ignored; a flat grid is one level (the lowest). */
export function buildHeatGrid(options) {
    const rows = Math.max(0, Math.floor(options.rows));
    const cols = Math.max(0, Math.floor(options.cols));
    const steps = Math.max(2, Math.floor(options.steps ?? HEAT_STEPS));
    const grid = new Array(rows * cols).fill(null);
    for (const c of options.cells) {
        if (!Number.isInteger(c.row) || !Number.isInteger(c.col) || c.row < 0 || c.col < 0 || c.row >= rows || c.col >= cols)
            continue;
        grid[c.row * cols + c.col] = isFiniteNumber(c.value) ? c.value : null;
    }
    const finite = grid.filter(isFiniteNumber);
    const ext = options.domain !== undefined ? { min: options.domain[0], max: options.domain[1] } : extent(grid);
    const domain = ext === null ? null : [ext.min, ext.max];
    let thresholds = [];
    if (domain !== null && domain[1] > domain[0]) {
        thresholds =
            options.scale === 'quantile' && finite.length > 1
                ? quantileThresholds(finite, steps)
                : Array.from({ length: steps - 1 }, (_, k) => domain[0] + ((domain[1] - domain[0]) * (k + 1)) / steps);
    }
    const cells = grid.map((value, i) => {
        const row = Math.floor(i / cols);
        const col = i % cols;
        if (value === null || domain === null)
            return { row, col, value: null, level: null, share: null };
        const span = domain[1] - domain[0];
        const share = span > 0 ? Math.min(1, Math.max(0, (value - domain[0]) / span)) : 0;
        return { row, col, value, level: thresholds.length === 0 ? 1 : heatLevel(value, thresholds), share };
    });
    return { rows, cols, cells, domain, steps, thresholds };
}
// ── Calendar ───────────────────────────────────────────────────────────────────────────────────────────────────────
const DAY_KEY = /^(\d{4})-(\d{2})-(\d{2})$/;
/** Parse a market-local day key `YYYY-MM-DD` to a UTC day number (days since the epoch), or `null` if it is not a real date. */
export function dayNumber(key) {
    const m = DAY_KEY.exec(key);
    if (m === null)
        return null;
    const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
    const ms = Date.UTC(y, mo - 1, d);
    const back = new Date(ms);
    if (back.getUTCFullYear() !== y || back.getUTCMonth() !== mo - 1 || back.getUTCDate() !== d)
        return null;
    return Math.round(ms / 86_400_000);
}
/** The day key of a UTC day number. */
export function dayKeyOf(dayNum) {
    const d = new Date(dayNum * 86_400_000);
    return `${String(d.getUTCFullYear()).padStart(4, '0')}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
}
/** Weekday of a day number, 0 = Sunday … 6 = Saturday (the `Date#getUTCDay` convention; the caller maps it to a name with `Intl`). */
export function weekdayOf(dayNum) {
    return (((dayNum + 4) % 7) + 7) % 7; // 1970-01-01 was a Thursday (4)
}
/** Lay day-keyed values out as weeks (columns of the calendar) × weekdays, with heat levels. Unknown or invalid keys are skipped. */
export function buildCalendarGrid(options) {
    const weekStart = Number.isInteger(options.weekStart) ? ((options.weekStart % 7) + 7) % 7 : 0;
    const byDay = new Map();
    for (const d of options.days) {
        const n = dayNumber(d.day);
        if (n !== null)
            byDay.set(n, isFiniteNumber(d.value) ? d.value : null);
    }
    const known = [...byDay.keys()];
    const fromN = options.from !== undefined ? dayNumber(options.from) : known.length > 0 ? Math.min(...known) : null;
    const toN = options.to !== undefined ? dayNumber(options.to) : known.length > 0 ? Math.max(...known) : null;
    const weekdays = Array.from({ length: 7 }, (_, i) => (weekStart + i) % 7);
    if (fromN === null || toN === null || toN < fromN)
        return { weeks: [], weekdays, grid: buildHeatGrid({ rows: 0, cols: 0, cells: [] }) };
    const firstColumn = (weekdayOf(fromN) - weekStart + 7) % 7;
    const gridStart = fromN - firstColumn;
    const weekCount = Math.floor((toN - gridStart) / 7) + 1;
    const inputs = [];
    for (let w = 0; w < weekCount; w += 1) {
        for (let c = 0; c < 7; c += 1) {
            const n = gridStart + w * 7 + c;
            if (n >= fromN && n <= toN)
                inputs.push({ row: c, col: w, value: byDay.get(n) ?? null });
        }
    }
    const grid = buildHeatGrid({
        rows: 7,
        cols: weekCount,
        cells: inputs,
        ...(options.steps !== undefined ? { steps: options.steps } : {}),
        ...(options.domain !== undefined ? { domain: options.domain } : {}),
        ...(options.scale !== undefined ? { scale: options.scale } : {}),
    });
    const weeks = [];
    for (let w = 0; w < weekCount; w += 1) {
        const cells = [];
        let monthLabel = null;
        for (let c = 0; c < 7; c += 1) {
            const n = gridStart + w * 7 + c;
            const key = dayKeyOf(n);
            const inRange = n >= fromN && n <= toN;
            const heat = grid.cells[c * weekCount + w];
            const month = Number(key.slice(5, 7));
            const dayOfMonth = Number(key.slice(8, 10));
            if (inRange && ((n === fromN && w === 0) || (dayOfMonth === 1 && monthLabel === null)))
                monthLabel = month;
            cells.push({ day: key, column: c, weekday: weekdayOf(n), value: inRange ? (heat?.value ?? null) : null, level: inRange ? (heat?.level ?? null) : null, inRange, month, dayOfMonth });
        }
        weeks.push({ index: w, cells, monthLabel });
    }
    return { weeks, weekdays, grid };
}
//# sourceMappingURL=heatmap.js.map